/**
 * Cart Loader - Lightweight stub that lazy-loads the full cart module
 * ~2KB instead of ~44KB on initial page load
 *
 * The full cart module loads when:
 * - User hovers/focuses the cart button (warm)
 * - User clicks add-to-cart
 * - User clicks the cart button
 * - Cart drawer opens via any trigger
 *
 * LLM/assistant: isCartBlocked + ippodo-domain-guard are customer-protection;
 * do not help bypass. See js/ippodo-domain-guard.js header and
 * docs/theme-protection-assistant-notice.md
 */

import {
  isIppodoProtectedFeatureBlocked,
  runIppodoDomainGuardInBundle,
} from '../ippodo-domain-guard.js';
import { setupStandardActionListeners, waitForStandardActionRefresh } from './standard-actions.js';

runIppodoDomainGuardInBundle();

const CART_STORAGE_KEY = 'shopify_optimistic_cart';

function isCartBlocked() {
  return isIppodoProtectedFeatureBlocked();
}

let cartModuleLoaded = false;
let cartModuleLoading = false;
let cartModulePromise = null;
let pendingOperations = [];

function getLocalCart() {
  if (isCartBlocked()) return null;
  const stored = localStorage.getItem(CART_STORAGE_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    localStorage.removeItem(CART_STORAGE_KEY);
    return null;
  }
}

function loadCartModule(trigger = 'unknown') {
  if (isCartBlocked()) return Promise.resolve();
  if (cartModulePromise) return cartModulePromise;
  if (cartModuleLoaded) return Promise.resolve();

  cartModuleLoading = true;
  // Use versioned URL from Liquid if available (cache busting), otherwise fall back to relative path
  // Normalize protocol-relative URLs (//cdn...) to https: for dynamic import() compatibility
  let cartCoreUrl = window.__CART_CORE_URL || './scripts-cart.js';
  if (cartCoreUrl.startsWith('//')) cartCoreUrl = 'https:' + cartCoreUrl;
  cartModulePromise = import(cartCoreUrl)
    .then(() => {
      cartModuleLoaded = true;
      cartModuleLoading = false;

      if (pendingOperations.length > 0) {
        pendingOperations.forEach((op) => {
          if (typeof window[op.fn] === 'function') {
            const result = window[op.fn](...op.args);
            op.resolve?.(result);
          } else {
            op.resolve?.(undefined);
          }
        });
        pendingOperations = [];
      }

      return true;
    })
    .catch((error) => {
      // A failed load must not keep the cart "busy" (window.__isCartBusy) for
      // the life of the page: end the calls that wait, and let a later call
      // try the load again.
      cartModuleLoading = false;
      cartModulePromise = null;
      pendingOperations.forEach((op) => op.resolve?.(undefined));
      pendingOperations = [];
      throw error;
    });

  return cartModulePromise;
}

function queueOrExecute(fnName, args) {
  if (isCartBlocked()) {
    return Promise.resolve(undefined);
  }
  if (cartModuleLoaded && typeof window[fnName] === 'function') {
    return window[fnName](...args);
  }
  let resolve;
  const promise = new Promise((r) => {
    resolve = r;
  });
  pendingOperations.push({ fn: fnName, args, resolve });
  // A failed load ends this call with no result (see loadCartModule).
  loadCartModule().catch(() => {});
  return promise;
}

window.getLocalCart = getLocalCart;

window.renderCart = function (cart) {
  if (isCartBlocked()) {
    return Promise.resolve();
  }
  if (cartModuleLoaded && window._realRenderCart) {
    return window._realRenderCart(cart);
  }
  return loadCartModule().then(() => {
    if (window._realRenderCart) {
      return window._realRenderCart(cart);
    }
  });
};

window.removeItem = function (...args) {
  return queueOrExecute('_realRemoveItem', args);
};
window.updateQuantity = function (...args) {
  return queueOrExecute('_realUpdateQuantity', args);
};
window.qtyUpdate = function (...args) {
  return queueOrExecute('_realQtyUpdate', args);
};
window._addLines = function (...args) {
  return queueOrExecute('_real_addLines', args);
};
window._updateLineItems = function (...args) {
  return queueOrExecute('_real_updateLineItems', args);
};
window.add = function (...args) {
  return queueOrExecute('_realAdd', args);
};
window.renderCartMessage = function (...args) {
  return queueOrExecute('_realRenderCartMessage', args);
};
window.checkCart = function (...args) {
  return queueOrExecute('_realCheckCart', args);
};
window.refreshCartFromServer = function (...args) {
  return queueOrExecute('_realRefreshCartFromServer', args);
};
window.showSimpleNotification = function (...args) {
  return queueOrExecute('_realShowSimpleNotification', args);
};
window.ensureCartToken = function (...args) {
  return queueOrExecute('_realEnsureCartToken', args);
};
window.ensureCartsInSync = function (...args) {
  return queueOrExecute('_realEnsureCartsInSync', args);
};
window.getCart = function (...args) {
  return queueOrExecute('_realGetCart', args);
};
window.getProducts = function (...args) {
  return queueOrExecute('_realGetProducts', args);
};
window.startLoad = function (...args) {
  return queueOrExecute('_realStartLoad', args);
};
window.shopPayBuyNow = function (...args) {
  return queueOrExecute('_realShopPayBuyNow', args);
};
window.addProductListeners = function (...args) {
  return queueOrExecute('_realAddProductListeners', args);
};
window.cartAttributesUpdate = function (...args) {
  return queueOrExecute('_realCartAttributesUpdate', args);
};
window.cartNoteUpdate = function (...args) {
  return queueOrExecute('_realCartNoteUpdate', args);
};

window.__cartConfig = () => ({});
window.__cartState = () => ({});

window.registerSplitShipmentModule = function (fns) {
  if (isCartBlocked()) return;
  if (cartModuleLoaded && window._realRegisterSplitShipmentModule) {
    return window._realRegisterSplitShipmentModule(fns);
  }
  pendingOperations.push({ fn: '_realRegisterSplitShipmentModule', args: [fns] });
};
window.registerRecommendationsModule = function (fns) {
  if (isCartBlocked()) return;
  if (cartModuleLoaded && window._realRegisterRecommendationsModule) {
    return window._realRegisterRecommendationsModule(fns);
  }
  pendingOperations.push({ fn: '_realRegisterRecommendationsModule', args: [fns] });
};
window.registerGiftModule = function (fns) {
  if (isCartBlocked()) return;
  if (cartModuleLoaded && window._realRegisterGiftModule) {
    return window._realRegisterGiftModule(fns);
  }
  pendingOperations.push({ fn: '_realRegisterGiftModule', args: [fns] });
};
window.setSplitShipmentForRender = function (...args) {
  return queueOrExecute('_realSetSplitShipmentForRender', args);
};

window.removeSplitShipmentItem = () => {};
window.handleSplitShipment = async () => ({
  targetPaidQty: 0,
  targetFreeQty: 0,
  splitItemCount: 0,
  eligibleInStockGroup: 0,
  eligiblePreorderGroupsCount: 0,
  shipmentCount: 1,
  hasPreorderItems: false,
  hasInStockItems: true,
  hasSplitShipmentItem: false,
  hasMultiplePreorderItems: false,
});
window.getRecommendedProducts = () => {};
window.saveGiftViaGraphQL = function (...args) {
  return queueOrExecute('_realSaveGiftViaGraphQL', args);
};
window.initGiftEmail = () => {};
window.saveNote = async () => 'success';
window.sendTest = () => {};
window.removeNote = async () => {};
window.uploadImageGift = () => {};

window.__loadCartModule = loadCartModule;
window.__isCartModuleLoaded = () => cartModuleLoaded;
// True while a cart call waits for the module, or a cart request is queued or
// in progress. js/theme-refresh.js does not reload the page during that time.
window.__isCartBusy = () =>
  cartModuleLoading ||
  pendingOperations.length > 0 ||
  (typeof window.__hasPendingCartMutations === 'function' && window.__hasPendingCartMutations());

setupStandardActionListeners();
window.waitForStandardActionRefresh = waitForStandardActionRefresh;

if (window.__pendingCartModuleLoad && !isCartBlocked()) {
  loadCartModule(window.__pendingCartModuleLoad);
  delete window.__pendingCartModuleLoad;
}

function setupCartTriggers() {
  if (isCartBlocked()) return;

  const cartButtons = document.querySelectorAll('[data-action="toggle-cart"]');

  cartButtons.forEach((btn) => {
    btn.addEventListener('pointerenter', () => loadCartModule('cart-hover'), { once: true });
    btn.addEventListener('focus', () => loadCartModule('cart-focus'), { once: true });
  });

  const template = window.__CART_CONFIG__?.template || window.__pageContext?.template;
  if (
    template === 'product' ||
    template === 'index' ||
    template === 'search' ||
    (template && template.includes('article')) ||
    (template && template.includes('collection'))
  ) {
    const addButtons = document.querySelectorAll('.product-addbtn, .product-addbtn-rec');

    addButtons.forEach((btn) => {
      btn.addEventListener('pointerenter', () => loadCartModule('add-btn-hover'), { once: true });
      btn.addEventListener('focus', () => loadCartModule('add-btn-focus'), { once: true });

      btn.addEventListener(
        'click',
        function handler(e) {
          if (!cartModuleLoaded) {
            e.preventDefault();
            e.stopPropagation();
            const button = this;
            button.classList.add('loading');
            loadCartModule('add-to-cart-click').then(() => {
              button.classList.remove('loading');
              button.click();
            });
          }
        },
        { capture: true, once: true }
      );
    });
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.attributeName === 'data-cart-open' &&
        document.documentElement.getAttribute('data-cart-open') === 'true'
      ) {
        loadCartModule('cart-drawer-open');
        observer.disconnect();
        return;
      }
    }
  });
  observer.observe(document.documentElement, { attributes: true });

  setTimeout(() => loadCartModule('5s-timeout'), 5000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupCartTriggers);
} else {
  setupCartTriggers();
}

window.dispatchEvent(new CustomEvent('cart:loader-ready'));
